<template>
    <div
        tabindex="-1"
        class="hidden overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 justify-center items-center w-full md:inset-0 h-[calc(100%-1rem)] max-h-full">
        <div class="relative p-4 w-full max-w-md max-h-full">
            <!-- Modal content -->
            <div
                class="relative bg-white rounded-lg shadow-sm dark:bg-gray-700">
                <!-- Modal header -->
                <div
                    class="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600 border-gray-200">
                    <h3
                        class="text-lg font-semibold text-gray-900 dark:text-white">
                        {{ $t('promocode.modal_title') }}
                    </h3>
                    <button
                        type="button"
                        ref="closeButtonElm"
                        class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white cursor-pointer"
                        data-modal-toggle="promocode-modal">
                        <i-tabler-x
                            class="w-6 h-6 text-gray-400 hover:text-gray-900"></i-tabler-x>
                    </button>
                </div>
                <!-- Modal body -->
                <form class="p-4 md:p-5" @submit="onSubmit">
                    <div class="grid gap-4 mb-4 grid-cols-2">
                        <div class="col-span-2">
                            <label
                                for="name"
                                class="block mb-2 font-medium text-gray-900 dark:text-white"
                                >{{ $t('promocode.input_label') }}</label
                            >
                            <input
                                type="text"
                                v-model="promocode"
                                class="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                                required />
                        </div>
                    </div>

                    <div class="flex items-center gap-3">
                        <button
                            type="submit"
                            :disabled="loading"
                            class="text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-cyan-300 dark:focus:ring-cyan-800 font-medium rounded-lg px-5 py-2.5 text-center inline-flex items-center gap-2 cursor-pointer">
                            <i-tabler-loader-2
                                v-if="loading"
                                class="w-5 h-5 animate-spin"></i-tabler-loader-2>
                            {{ $t('promocode.save_button_label') }}
                        </button>
                        <div v-if="message">
                            {{ message }}
                        </div>
                    </div>

                    <div class="mt-8" v-if="props.haveValidPromocode">
                        <div class="text-sm text-gray-500 dark:text-gray-400">
                            {{ $t('promocode.have_valid_code_message') }}
                            <a
                                href=""
                                @click.prevent="onRemoveCodeClick"
                                class="underline hover:no-underline text-orange-400"
                                >{{ $t('promocode.remove_button_label') }}</a
                            >
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { setPromocode, validatePromocode } from '~/helpers/payments';
import { isDevelopment } from '~/helpers/utils';

interface Props {
    haveValidPromocode: boolean;
    userEmail?: string;
}

const props = withDefaults(defineProps<Props>(), {});

const promocode = ref('');
const loading = ref(false);
const message = ref('');
const closeButtonElm = ref<HTMLButtonElement | null>(null);

const emit = defineEmits(['got-valid-promocode', 'removed-promocode']);

const onSubmit = async (e: Event) => {
    e.preventDefault();
    loading.value = true;
    message.value = '';

    const valid = await validatePromocode(promocode.value, props.userEmail);

    if (valid) {
        // Promocode is valid
        if (isDevelopment()) {
            console.log('[DEV] Promocode is valid');
        }
        setPromocode(promocode.value);
        promocode.value = '';
        closeButtonElm.value?.click();
        emit('got-valid-promocode');
    } else {
        // Promocode is invalid
        if (isDevelopment()) {
            console.log('[DEV] Promocode is invalid');
        }
        message.value = 'Invalid code';
    }

    loading.value = false;
};

const onRemoveCodeClick = () => {
    setPromocode('');
    emit('removed-promocode');
};
</script>
